"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
function MarkerStroke() {
  return <svg aria-hidden="true" viewBox="0 0 220 22" preserveAspectRatio="none" className="pointer-events-none absolute left-0 right-0 -bottom-1 h-[0.35em] w-full" style={{
    color: "var(--accent)"
  }}>
      <path d="M2 13 C 40 4, 90 18, 150 9 S 210 14, 218 8" stroke="currentColor" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.45" />
    </svg>;
}
function StoriesGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="animate-pulse">
            <div className="rounded-lg" style={{
          backgroundColor: "var(--muted)",
          aspectRatio: "5 / 6"
        }} />
            <div className="pt-6 space-y-3">
              <div className="h-4 w-24 rounded-lg" style={{
            backgroundColor: "var(--muted)"
          }} />
              <div className="h-8 w-full rounded" style={{
            backgroundColor: "var(--muted)"
          }} />
              <div className="h-4 w-3/4 rounded" style={{
            backgroundColor: "var(--muted)"
          }} />
            </div>
          </div>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <div className="mt-16">
        <p style={{
        color: "var(--muted-foreground)"
      }} className="font-sans text-base">
          No articles available at this time.
        </p>
      </div>;
  }
  const displayPosts = posts.slice(0, 3);
  const stripHtml = (html?: string) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").trim();
  };
  return <div className="mt-12 grid grid-cols-1 gap-10 md:mt-16 md:grid-cols-3 md:gap-8">
      {displayPosts.map((post, i) => {
      const excerpt = stripHtml(post.excerpt);
      const truncatedExcerpt = excerpt.length > 140 ? excerpt.slice(0, 140).trim() + "…" : excerpt;
      const category = post.categories && post.categories.length > 0 ? post.categories[0] : null;
      const wordCount = stripHtml(post.content).split(/\s+/).filter(Boolean).length;
      const readTime = Math.max(2, Math.ceil(wordCount / 220));
      return <article key={post.id} data-index={i} className="group flex flex-col">
            <Link to={post.link || "#"} className="block">
              <div className="overflow-hidden rounded-lg" style={{
            aspectRatio: "5 / 6",
            backgroundColor: "var(--card)"
          }}>
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" /> : <div className="flex h-full w-full items-center justify-center" style={{
              backgroundColor: "var(--card)"
            }}>
                    <span className="font-serif text-4xl italic" style={{
                color: "var(--muted-foreground)"
              }}>
                      YE
                    </span>
                  </div>}
              </div>
            </Link>

            <div className="pt-6">
              <div className="flex flex-wrap items-center gap-3">
                {category && <span className="inline-flex items-center rounded-full border px-3 py-1 font-sans text-[11.5px] font-semibold uppercase" style={{
              borderColor: "var(--secondary)",
              backgroundColor: "var(--secondary)",
              color: "var(--primary)",
              letterSpacing: "0.08em"
            }}>
                    {category.name}
                  </span>}
                <span className="font-sans text-xs" style={{
              color: "var(--muted-foreground)",
              letterSpacing: "0.05em"
            }}>
                  {readTime} min read
                </span>
              </div>

              <h3 className="mt-5 font-serif font-semibold" style={{
            color: "var(--foreground)",
            fontSize: "1.625rem",
            lineHeight: "1.2",
            letterSpacing: "-0.02em"
          }}>
                <Link to={post.link || "#"} className="relative inline bg-[length:0%_2px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 ease-out group-hover:bg-[length:100%_2px]" style={{
              backgroundImage: `linear-gradient(to right, var(--primary), var(--primary))`
            }}>
                  {post.title}
                </Link>
              </h3>

              {truncatedExcerpt && <p className="mt-4 font-sans" style={{
            color: "var(--muted-foreground)",
            fontSize: "1.0625rem",
            lineHeight: "1.6"
          }}>
                  {truncatedExcerpt}
                </p>}
            </div>
          </article>;
    })}
    </div>;
}
export default function Insights() {
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  });
  return <section data-section-id="30641" id="seo-insights" data-nav="light" className="w-full py-20 md:py-32 bg-background text-foreground">
      <div className="mx-auto w-full" style={{
      maxWidth: "80rem",
      paddingLeft: "clamp(1.25rem, 4vw, 2rem)",
      paddingRight: "clamp(1.25rem, 4vw, 2rem)"
    }}>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end md:gap-8">
          <div className="md:col-span-3">
            <span className="font-sans text-[15px] font-semibold uppercase" style={{
            color: "var(--muted-foreground)",
            letterSpacing: "0.08em"
          }}>
              SEO Insights
            </span>
          </div>

          <div className="md:col-span-7">
            <h2 className="font-serif font-bold" style={{
            color: "var(--foreground)",
            fontSize: "clamp(1.8125rem, 2.8vw, 2.375rem)",
            lineHeight: "1.1",
            letterSpacing: "-0.03em"
          }}>
              <span>{"Modern "}</span>
              <span className="relative inline-block">
                <span className="relative z-10">SEO Strategy</span>
                <MarkerStroke />
              </span>
            </h2>
          </div>

          <div className="md:col-span-2 md:justify-self-end">
            <Link to="/BlogPage">
              <Button variant="outline" className="h-auto rounded-md border-[1px] bg-transparent px-6 py-3 font-sans text-sm font-semibold hover:bg-transparent" style={{
              borderColor: "var(--foreground)",
              color: "var(--foreground)"
            }}>
                <span>View All</span>
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <StoriesGrid />
        </PostsProvider>
      </div>
    </section>;
}